import type { Config } from 'next-i18n-router/dist/types';

import {
    EN_CA_LANG_ID,
    EN_CA_LOCALE,
    EN_US_LANG_ID,
    EN_US_LOCALE,
    FR_CA_LANG_ID,
    FR_CA_LOCALE,
} from '@/constants/index';

import { languageMap } from './constants';

// Zod validation should ensure type is string
const DEFAULT_LANGUAGE = process.env.DEFAULT_LANGUAGE as string;
const SUPPORTED_LANGUAGES = process.env.SUPPORTED_LANGUAGES?.split(',') || [];

export const i18nConfig: Config = {
    // cookieOptions: TODO, look into options to match WCS
    locales: SUPPORTED_LANGUAGES,
    defaultLocale: DEFAULT_LANGUAGE || '',
    noPrefix: true,
    localeDetector: (request, _config) => {
        const { searchParams } = new URL(request.url);
        const langId = searchParams.get('langId');

        let requested: string | undefined;

        if (langId) {
            // Radix 10 explicitly: without it `-0x18` parses as -24 and
            // becomes an undocumented alias for en-ca, which the middleware's
            // string comparison of the same param would not honour.
            requested = languageMap[parseInt(langId, 10)];
        } else {
            const langCookie = request.headers
                .get('cookie')
                ?.match(/selectedLanguage=(-\d+);/);

            // Get locale from cookie
            switch (langCookie?.[1]) {
                case EN_US_LANG_ID:
                    requested = EN_US_LOCALE;
                    break;
                case EN_CA_LANG_ID:
                    requested = EN_CA_LOCALE;
                    break;
                case FR_CA_LANG_ID:
                    requested = FR_CA_LOCALE;
                    break;
            }
        }

        // The language ids are shared across regions, so a caller can ask a US
        // build for en-ca/fr-ca. Resolve the fallback the same way the
        // middleware does, so both locale entry points agree on what this
        // deployment serves.
        return (
            resolveSupportedLocale(requested) ||
            resolveSupportedLocale(DEFAULT_LANGUAGE) ||
            DEFAULT_LANGUAGE
        );
    },
};

/**
 * Narrows a caller-supplied locale to one this deployment actually serves.
 *
 * `langId`, the `selectedLanguage` cookie and the `locale` query param are all
 * caller-controlled, and the language ids are shared across regions: `-24`
 * (en-ca) and `-25` (fr-ca) are perfectly well formed on a US build that only
 * publishes `en-us`. Every ContentStack helper in `services/content/data`
 * rethrows when handed a locale that isn't on the stack, so an unsupported
 * value has to be stopped here rather than surfacing as a 500 several layers
 * down (in an API route, in `generateMetadata`, or inside the locale-keyed
 * Forge providers in the customers layout, which have no error boundary above
 * them).
 *
 * Matching is case insensitive and the configured spelling is returned, so
 * callers always hand ContentStack a locale in the exact form the deployment
 * declared in SUPPORTED_LANGUAGES.
 *
 * @returns the supported locale, or undefined when this build doesn't serve it
 */
export function resolveSupportedLocale(
    locale: string | null | undefined,
): string | undefined {
    if (!locale) {
        return undefined;
    }

    const normalized = locale.toLowerCase();

    return i18nConfig.locales.find(
        supported => supported.toLowerCase() === normalized,
    );
}
